import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { knowledgeApi, type KnowledgeSource } from '@/lib/api';
import { BookOpen, Plus, Search, X, ChevronRight, FileText, Tag, Zap, Layers } from 'lucide-react';
import { Spinner } from '@/components/ui/Spinner';
import { toast } from '@/components/ui/toast';
import { PageGuide } from '@/components/PageGuide';

export const Route = createFileRoute('/_auth/knowledge/')({ component: KnowledgePage });

export default function KnowledgePage() {
  const qc = useQueryClient();
  const { data: sources = [], isLoading } = useQuery({ queryKey: ['knowledge-sources'], queryFn: knowledgeApi.listSources });

  const [search, setSearch]               = useState('');
  const [showCreate, setShowCreate]       = useState(false);
  const [searchQuery, setSearchQuery]     = useState('');
  const [searchResults, setSearchResults] = useState<Array<{ articleTitle: string; content: string; similarity: number }> | null>(null);
  const [searching, setSearching]         = useState(false);

  const createMutation = useMutation({
    mutationFn: knowledgeApi.createSource,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['knowledge-sources'] });
      setShowCreate(false);
      toast.success('Knowledge source created');
    },
  });

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const results = await knowledgeApi.search({ query: searchQuery, topN: 6 });
      setSearchResults(results as any);
    } finally { setSearching(false); }
  };

  const filtered = sources.filter((s: KnowledgeSource) =>
    !search || s.name.toLowerCase().includes(search.toLowerCase()),
  );

  const totalArticles = sources.reduce((n: number, s: KnowledgeSource) => n + (s._count?.articles ?? 0), 0);
  const totalTopics   = sources.reduce((n: number, s: KnowledgeSource) => n + (s._count?.topics ?? 0), 0);

  return (
    <div className="page">
      <PageGuide pageKey="knowledge" />

      {/* ── Header ─────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#e2e8f0' }}>Knowledge Base</h1>
          </div>
          {/* Inline stats – compact pills */}
          {!isLoading && sources.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {[
                { icon: Layers,   val: sources.length,  label: 'sources',  c: '#818cf8' },
                { icon: FileText, val: totalArticles,   label: 'articles', c: '#38bdf8' },
                { icon: Tag,      val: totalTopics,     label: 'topics',   c: '#34d399' },
              ].map(({ icon: Icon, val, label, c }) => (
                <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 20, background: `${c}12`, border: `1px solid ${c}28`, fontSize: 12, fontWeight: 600, color: c }}>
                  <Icon size={11} /> {val} {label}
                </span>
              ))}
            </div>
          )}
        </div>
        <button
          id="new-knowledge-btn"
          onClick={() => setShowCreate(true)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 16px', background: 'var(--color-primary)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: '0 2px 12px var(--color-primary-glow)', flexShrink: 0 }}
        >
          <Plus size={14} /> New Source
        </button>
      </div>

      {/* ── Body: two columns ──────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 16, alignItems: 'start' }}>

        {/* ── LEFT: source list ─── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

          {/* Section toolbar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#334155', pointerEvents: 'none' }} />
              <input
                id="knowledge-sources-search"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Filter sources…"
                style={{ width: '100%', padding: '7px 12px 7px 30px', background: '#0a0e1c', border: '1px solid #1a1f35', borderRadius: 8, color: '#e2e8f0', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
          </div>

          {/* States */}
          {isLoading && <div style={{ padding: '48px 0', textAlign: 'center' }}><Spinner size="md" /></div>}

          {!isLoading && filtered.length === 0 && (
            <div style={{ padding: '48px 24px', textAlign: 'center', border: '1px dashed #1e2130', borderRadius: 12, color: '#475569', background: '#0a0e1c' }}>
              <BookOpen size={28} style={{ opacity: 0.2, display: 'block', margin: '0 auto 10px' }} />
              <p style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600 }}>
                {search ? `No sources match "${search}"` : 'No knowledge sources yet'}
              </p>
              <p style={{ margin: '0 0 14px', fontSize: 12, color: '#334155' }}>
                {search ? 'Try a different filter.' : 'Create a source and add articles for your agents to search.'}
              </p>
              {!search && (
                <button onClick={() => setShowCreate(true)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: 'var(--color-primary)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  <Plus size={12} /> Create first source
                </button>
              )}
            </div>
          )}

          {!isLoading && filtered.map((source: KnowledgeSource) => (
            <Link
              key={source.id}
              to="/knowledge/$sourceId"
              params={{ sourceId: source.id }}
              id={`knowledge-source-${source.id}`}
              className="kbSourceRow"
              style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', background: '#0a0e1c', border: '1px solid #1a1f35', borderRadius: 12, transition: 'all 0.15s' }}
            >
              {/* Icon */}
              <div style={{ width: 42, height: 42, borderRadius: 11, background: 'linear-gradient(135deg, #0ea5e918, #6366f128)', border: '1px solid #6366f125', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 19, flexShrink: 0 }}>
                📚
              </div>

              {/* Text */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {source.name}
                  </span>
                  {!source.active && (
                    <span style={{ padding: '1px 7px', borderRadius: 10, fontSize: 10, fontWeight: 600, background: '#1e2130', color: '#475569', border: '1px solid #1e2840', flexShrink: 0 }}>Inactive</span>
                  )}
                </div>
                {source.description && (
                  <p style={{ margin: '0 0 8px', fontSize: 12, color: '#64748b', lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {source.description}
                  </p>
                )}
                <div style={{ display: 'flex', gap: 6 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 10, background: '#0ea5e910', border: '1px solid #0ea5e920', fontSize: 11, color: '#38bdf8', fontWeight: 600 }}>
                    <FileText size={9} />{source._count?.articles ?? 0} articles
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 10, background: '#10b98110', border: '1px solid #10b98120', fontSize: 11, color: '#34d399', fontWeight: 600 }}>
                    <Tag size={9} />{source._count?.topics ?? 0} topics
                  </span>
                </div>
              </div>

              {/* Arrow */}
              <ChevronRight size={15} style={{ color: '#334155', flexShrink: 0, transition: 'color 0.15s' }} className="kbArrow" />
            </Link>
          ))}
        </div>

        {/* ── RIGHT: vector search panel ─── */}
        <div style={{ position: 'sticky', top: 16 }}>
          <div style={{ background: '#0a0e1c', border: '1px solid #1a1f35', borderRadius: 12, overflow: 'hidden' }}>
            {/* Panel header */}
            <div style={{ padding: '14px 16px 12px', borderBottom: '1px solid #1a1f35', display: 'flex', alignItems: 'center', gap: 9 }}>
              <div style={{ width: 26, height: 26, borderRadius: 7, background: '#6366f115', border: '1px solid #6366f125', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Zap size={12} style={{ color: '#818cf8' }} />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#c7d2fe', lineHeight: 1 }}>Vector Search Test</div>
                <div style={{ fontSize: 11, color: '#334155', marginTop: 2 }}>Simulate semantic retrieval</div>
              </div>
            </div>

            {/* Search input */}
            <div style={{ padding: '12px 14px', borderBottom: searchResults ? '1px solid #1a1f35' : 'none' }}>
              <div style={{ position: 'relative', marginBottom: 8 }}>
                <Search size={12} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#334155', pointerEvents: 'none' }} />
                <input
                  id="knowledge-search-test"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSearch()}
                  placeholder="e.g. what is the return policy?"
                  style={{ width: '100%', padding: '8px 10px 8px 30px', background: '#0f1422', border: '1px solid #1e2840', borderRadius: 7, color: '#e2e8f0', fontSize: 12, outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  id="kb-search-btn"
                  onClick={handleSearch}
                  disabled={searching || !searchQuery.trim()}
                  style={{ flex: 1, padding: '7px', background: searchQuery.trim() ? '#6366f1' : '#1a1f35', border: 'none', borderRadius: 7, color: searchQuery.trim() ? '#fff' : '#334155', fontSize: 12, fontWeight: 600, cursor: searchQuery.trim() ? 'pointer' : 'default', transition: 'all 0.15s' }}
                >
                  {searching ? <Spinner size="sm" /> : 'Search'}
                </button>
                {searchResults && (
                  <button onClick={() => { setSearchResults(null); setSearchQuery(''); }} style={{ padding: '7px 9px', background: 'transparent', border: '1px solid #1e2130', borderRadius: 7, color: '#475569', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                    <X size={12} />
                  </button>
                )}
              </div>
            </div>

            {/* Results */}
            {searchResults && (
              <div style={{ maxHeight: 460, overflowY: 'auto' }}>
                {searchResults.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#475569', fontSize: 12 }}>
                    No matching chunks found
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    {searchResults.map((r, i) => (
                      <div key={i} style={{ padding: '11px 14px', borderBottom: i < searchResults.length - 1 ? '1px solid #12172a' : 'none' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 5 }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#c7d2fe', lineHeight: 1.3, flex: 1 }}>{r.articleTitle}</span>
                          <span style={{ fontSize: 10, fontWeight: 700, color: '#34d399', background: '#10b98112', border: '1px solid #10b98122', padding: '2px 6px', borderRadius: 8, flexShrink: 0 }}>
                            {((r.similarity ?? 0) * 100).toFixed(0)}%
                          </span>
                        </div>
                        <p style={{ margin: 0, fontSize: 11, color: '#475569', lineHeight: 1.55, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' as const, overflow: 'hidden' }}>
                          {r.content.slice(0, 200)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Create Drawer ── */}
      {showCreate && (
        <CreateSourceDrawer
          onClose={() => setShowCreate(false)}
          onCreate={data => createMutation.mutate(data)}
          isPending={createMutation.isPending}
        />
      )}

      <style>{`
        .kbSourceRow:hover {
          border-color: #6366f140 !important;
          box-shadow: 0 2px 16px rgba(99,102,241,0.07);
          transform: translateY(-1px);
        }
        .kbSourceRow:hover .kbArrow { color: #6366f1 !important; }
      `}</style>
    </div>
  );
}

function CreateSourceDrawer({
  onClose, onCreate, isPending,
}: {
  onClose: () => void;
  onCreate: (d: { name: string; description: string }) => void;
  isPending: boolean;
}) {
  const [name, setName]               = useState('');
  const [description, setDescription] = useState('');
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', zIndex: 40 }} />
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '100%', maxWidth: 420, background: '#0d1022', borderLeft: '1px solid #1e2130', zIndex: 50, padding: '28px 24px', boxShadow: '-16px 0 60px rgba(0,0,0,0.6)', animation: 'drawerIn 0.22s cubic-bezier(0.34,1.2,0.64,1)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#e2e8f0' }}>New Knowledge Source</h2>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: '#475569' }}>Articles in this source will be indexed for semantic search</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', fontSize: 20, lineHeight: 1 }}>×</button>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={lbl}>Name <span style={{ color: '#f87171' }}>*</span></label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Product Documentation" autoFocus style={inp} />
        </div>
        <div style={{ marginBottom: 24 }}>
          <label style={lbl}>Description</label>
          <input value={description} onChange={e => setDescription(e.target.value)} placeholder="Official product docs and FAQs" style={inp} />
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '9px', background: 'transparent', border: '1px solid #1e2130', borderRadius: 8, color: '#64748b', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
          <button onClick={() => onCreate({ name, description })} disabled={!name.trim() || isPending} style={{ flex: 2, padding: '9px', background: 'var(--color-primary)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: !name.trim() ? 0.5 : 1 }}>
            {isPending ? <Spinner size="sm" /> : 'Create source'}
          </button>
        </div>
      </div>
      <style>{`@keyframes drawerIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
    </>
  );
}

const lbl: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: '#94a3b8', marginBottom: 5 };
const inp: React.CSSProperties = { width: '100%', padding: '9px 12px', background: '#0f1221', border: '1px solid #1e2130', borderRadius: 8, color: '#e2e8f0', fontSize: 13, outline: 'none', boxSizing: 'border-box' };
